import React, { Component } from 'react'
import { connect } from 'react-redux'
import { injectIntl } from 'react-intl'
import cn from 'classnames'
import { FormControl, Input, InputLabel, IconButton } from '@material-ui/core'
import DescriptionIcon from '@material-ui/icons/Description'
import ClearIcon from '@material-ui/icons/Clear'
import isEqual from 'lodash/isEqual'
import messages from 'routes/App/Apps/LogbookForm/translate'
import { showNotice } from 'reducers/notice'
import NoticeHelper from 'helpers/notice'
import imageCompression from 'browser-image-compression'
import { uploadFiles, removeFiles } from 'reducers/common/files/reducer'
import withPromise from 'helpers/withPromise'
import { MY_JOBS_UPLOAD_FILES } from 'constants/apiRouting'
import { TABLET_BREAKPOINT } from 'helpers/responsive'
import ImageModal from 'components/common/Modals/ImageModal'
import ConfirmDialog from 'components/common/Popup/ConfirmDialog'
import { ReactComponent as DownloadIcon } from 'assets/img/download-icon.svg'
import { ReactComponent as CloseIcon } from 'assets/img/close-icon.svg'
import { ReactComponent as ViewMoreIcon } from 'assets/img/view-more-icon.svg'
import { ReactComponent as RemoveIcon } from 'assets/img/remove-icon.svg'
import { ReactComponent as UploadIcon } from 'assets/img/upload-icon.svg'
import ifWindowWidthLessThan from 'helpers/ifWindowWidthLessThan'
import ReactImageAppear from 'react-image-appear'
// import FadeInImage from 'components/common/FadeInImage'

class JobFileInput extends Component {
  state = {
    value: {},
    oldValue: {},
    isOpen: false,
    isDeleteConfirmOpen: false,
    evtTaskInputId: '',
    isUpdating: false,
  }

  componentDidMount() {
    const {
      inputData: { value, evtInputId },
    } = this.props

    this.setState({
      value,
      oldValue: value,
      evtTaskInputId: evtInputId,
    })
  }

  shouldComponentUpdate(nextProps) {
    const { inputInfo, inputData } = this.props
    const { inputInfo: nextInputInfo, inputData: nextInputData } = nextProps

    if (
      !isEqual(inputInfo, nextInputInfo) ||
      !isEqual(inputData, nextInputData)
    ) {
      const { evtInputId, value = {} } = nextInputData

      this.setState({
        value,
        oldValue: value,
        evtTaskInputId: evtInputId,
      })

      return false
    }

    return true
  }

  openDeleteConfirm = e => {
    e.preventDefault()
    e.stopPropagation()
    this.setState({ isDeleteConfirmOpen: true })
  }

  closeDeleteConfirm = () => {
    this.setState({ isDeleteConfirmOpen: false })
  }

  onConfirmRemoveFile = () => {
    const {
      inputData: { evtInputId },
    } = this.props
    const data = {}
    data.evtInputId = evtInputId
    data.result = null

    this.setState(
      {
        value: null,
        isDeleteConfirmOpen: false,
      },
      this.onInputBlur,
    )
  }

  onInputBlur = () => {
    const {
      inputInfo: { id },
      inputData: { evtInputId },
      onJobTaskAutoSave,
    } = this.props
    const { value, oldValue, evtTaskInputId } = this.state

    if (value !== oldValue) {
      this.setState({
        isUpdating: true,
      })

      onJobTaskAutoSave(
        {
          evtInputId: evtInputId || evtTaskInputId,
          result: value ? value.id : null,
          input: id,
          file: value,
          setResultId: this.setResultId,
        },
        () => {
          this.setState({
            isUpdating: false,
          })
        },
      )
    }
  }

  handleModalChange = () => {
    this.setState(prevState => ({ isOpen: !prevState.isOpen }))
  }

  onChangeFile = async e => {
    const {
      showNotice,
      uploadFiles,
      intl: { formatMessage },
    } = this.props
    const [file] = e.currentTarget.files

    if (!file) {
      return false
    }

    this.props.onJobTaskFocusedInput(true)

    try {
      // Define compression options
      const options = {
        initialQuality: 0.9, // Compress quality to 90%
        maxWidthOrHeight: 1024, // Max width or height of the image
        useWebWorker: true, // Enable webworker for better performance
      }

      const fileForm = new FormData()

      if (file.type.startsWith('image/')) {
        // Compress the image file
        const compressedFile = await imageCompression(file, options)
        const renamedFile = new File([compressedFile], encodeURI(file.name))
        fileForm.append('file', renamedFile)
      } else {
        fileForm.append('file', file)
      }

      withPromise(uploadFiles, {
        file: fileForm,
        partUrl: MY_JOBS_UPLOAD_FILES,
        formatMessage,
      }).then(response => {
        if (response.error) {
          showNotice({
            notice: NoticeHelper.createErrorObj(
              formatMessage(messages.fileNotUploadMessage),
            ),
          })
        } else {
          this.setState(
            {
              value: response,
            },
            () => {
              this.onInputBlur()
            },
          )
        }
      })
    } catch (error) {
      console.error('Error while compressing the image:', error)
      showNotice({
        notice: NoticeHelper.createErrorObj(
          formatMessage(messages.fileNotUploadMessage),
        ),
      })
    }
  }

  handleDownload = async e => {
    e.preventDefault()
    const {
      value: { s3DownloadUri },
    } = this.state
    const link = document.createElement('a')
    link.download = 'image'
    link.href = s3DownloadUri

    if (ifWindowWidthLessThan(TABLET_BREAKPOINT)) {
      link.target = '_blank'
    }

    document.body.appendChild(link)
    link.click()
    link.remove()
  }

  setResultId = ({ id }) => {
    this.setState({
      evtTaskInputId: id,
    })
  }

  render() {
    const {
      index,
      disabled,
      className,
      isTaskCompleted,
      isFocusedInput,
      inputInfo: { title, required },
      intl: { formatMessage },
    } = this.props
    const {
      value,
      isOpen,
      isDeleteConfirmOpen,
      evtTaskInputId,
      isUpdating,
    } = this.state
    const uploadedFile =
      value && Object.prototype.hasOwnProperty.call(value, 'originalName')
    const inputId = `jobFile${index}`
    const fileInputKey = uploadedFile
      ? decodeURI(encodeURI(value.originalName))
      : `${evtTaskInputId}_${inputId}`
    const isObjExist = value && Object.keys(value).length > 0
    const isImage = isObjExist && value.mimeType.includes('image')
    const drawAsterisk = required ? '*' : ''
    const isInputDisabled = disabled || isUpdating || isFocusedInput

    return (
      <FormControl fullWidth className={className}>
        <span className="job-field-label">
          {title} {drawAsterisk}
        </span>
        <InputLabel
          htmlFor={inputId}
          className="job-field-label job-file-title">
          {!uploadedFile && (
            <div
              className={cn('job-field-input', 'no-file', {
                disabled: isInputDisabled,
              })}>
              <UploadIcon className="job-field-icon" />
              {formatMessage(messages.uploadPdfPngJpg)}
            </div>
          )}
          {Boolean(uploadedFile) && (
            <div className={cn('job-field-input', 'file', { isImage })}>
              {!isImage ? (
                <>
                  <DescriptionIcon className="job-field-icon" />
                  <a
                    href={value.uri}
                    title={decodeURI(encodeURI(value.originalName))}
                    target="_blank"
                    rel="noopener noreferrer">
                    {decodeURI(encodeURI(value.originalName))}
                  </a>
                </>
              ) : (
                <div className={cn('image-preview')}>
                  {/* <FadeInImage */}
                  {/*  src={value.uri} */}
                  {/*  animationDuration="1s" */}
                  {/*  placeholderStyle={{ backgroundColor: 'transparent' }} */}
                  {/* /> */}
                  <ReactImageAppear
                    src={value.uri}
                    animationDuration="1s"
                    placeholderStyle={{ backgroundColor: 'transparent' }}
                  />
                  <span className="image-title">
                    {decodeURI(encodeURI(value.originalName))}
                  </span>
                  <div className="btn-wrap">
                    <IconButton
                      className="download-button"
                      onClick={this.handleDownload}>
                      <DownloadIcon />
                    </IconButton>
                    <IconButton onClick={this.handleModalChange}>
                      <ViewMoreIcon />
                    </IconButton>
                  </div>
                  {!isTaskCompleted && (
                    <IconButton
                      className="delete-button"
                      onClick={this.openDeleteConfirm}>
                      <RemoveIcon />
                    </IconButton>
                  )}
                </div>
              )}
              {!isImage && !disabled && (
                <ClearIcon
                  className="job-field-icon job-field-clear-icon"
                  onClick={this.openDeleteConfirm}
                />
              )}
            </div>
          )}
          {!isImage && (
            <Input
              key={fileInputKey}
              id={inputId}
              type="file"
              onChange={this.onChangeFile}
              className="job-field-file-input"
              inputProps={{
                accept:
                  'image/jpeg,image/png,image/jpg,image/tiff,.tif,.tiff,application/pdf,application/x-pdf',
              }}
              disabled={isInputDisabled}
            />
          )}
        </InputLabel>
        {isImage && (
          <ImageModal open={isOpen} onClose={this.handleModalChange}>
            <div className="modal-content">
              <div className="image-wrap">
                {/* <FadeInImage */}
                {/*  src={value.uri} */}
                {/*  placeholderStyle={{ backgroundColor: 'transparent' }} */}
                {/* /> */}
                <ReactImageAppear
                  src={value.uri}
                  animationDuration="2s"
                  placeholderStyle={{ backgroundColor: 'transparent' }}
                />
              </div>
              <div className="btn-wrap">
                <IconButton onClick={this.handleModalChange}>
                  <CloseIcon />
                </IconButton>
              </div>
            </div>
          </ImageModal>
        )}
        <ConfirmDialog
          open={isDeleteConfirmOpen}
          title={formatMessage(messages.delete)}
          message={formatMessage(messages.deleteFileConfirmMessage)}
          className="dialog-primary-custom"
          onClose={this.closeDeleteConfirm}
          onConfirmAction={this.onConfirmRemoveFile}
          cancel={{
            color: 'default',
            variant: 'outlined',
            customText: formatMessage(messages.cancel),
          }}
          confirm={{
            color: 'scales-secondary',
            customText: formatMessage(messages.delete),
          }}
        />
      </FormControl>
    )
  }
}

export default connect(null, {
  showNotice,
  uploadFiles,
  removeFiles,
})(injectIntl(JobFileInput))
